
import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: "How is pricing calculated?",
    answer:
      "Pricing varies by location, service type, and time of booking. Most services have a minimum first hour charge, then half-hour increments. After-hours bookings (6pm-6am) include a 20% uplift, and short-notice after-hours bookings add a £50 late callout fee. Enter your postcode above to see exact rates for your area.",
  },
  {
    question: "Are there any hidden fees?",
    answer:
      "No hidden fees ever. The price you see when booking is the price you pay. All costs including service rates, after-hours charges, and any applicable fees are clearly displayed before you confirm your booking.",
  },
  {
    question: "What's included in the hourly rate?",
    answer:
      "The hourly rate includes the professional's time, basic tools, and travel to your location. For specialized services requiring specific materials (like plumbing parts or electrical components), materials are charged separately and discussed with you before purchase.",
  },
  {
    question: "Do I pay for travel time?",
    answer:
      "No, travel time is not charged separately for most services. The hourly rate covers the professional's time once they arrive at your location. For transportation and moving services, mileage is charged at £1.50 per mile.",
  },
  {
    question: "What is the minimum booking time?",
    answer:
      "Most services have a minimum first hour charge. After the first hour, time is billed in half-hour increments. This ensures professionals are fairly compensated for their time and travel.",
  },
  {
    question: "How does after-hours pricing work?",
    answer:
      "After-hours service (6pm-6am) includes a 20% uplift on the standard hourly rate. If you book after-hours service on short notice, there's an additional £50 late callout fee. These charges help ensure availability of professionals during non-standard hours.",
  },
  {
    question: "Why do prices vary by city?",
    answer:
      "Pricing reflects local market conditions including cost of living, demand for services, and professional availability in each area. This ensures fair compensation for professionals while keeping rates competitive for customers in different regions.",
  },
  {
    question: "How is waste removal priced?",
    answer:
      "Waste removal has a £50 booking fee plus charges based on load size: Quarter load (£90), Half load (£180), Three-quarter load (£270), or Full load (£360). During booking, describe your waste and our professionals will help determine the appropriate load size.",
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "We accept all major credit and debit cards through our secure payment system. Payment is processed when you book, with a deposit taken upfront and the remaining balance charged after service completion.",
  },
  {
    question: "When do I pay for the service?",
    answer:
      "A deposit is taken when you book to secure your appointment. The remaining balance is charged automatically after the service is completed and you've confirmed satisfaction with the work.",
  },
  {
    question: "Can I get a refund if I cancel?",
    answer:
      "Cancellation policies vary by timing. Cancel at least 24 hours before your appointment for a full refund. Cancellations within 24 hours may incur a cancellation fee. See our full cancellation and refund policy for details.",
  },
  {
    question: "Do you offer discounts or promotions?",
    answer:
      "We occasionally offer promotional discounts and discount codes. Follow us on social media or sign up for our newsletter to stay informed about special offers. Some services may also offer reduced rates for recurring bookings.",
  },
  {
    question: "Is there a price difference for emergency services?",
    answer:
      "Emergency services booked during after-hours (6pm-6am) include the standard 20% after-hours uplift. Short-notice emergency bookings also include a £50 late callout fee to ensure rapid professional availability when you need it most.",
  },
  {
    question: "How do I know the final cost before booking?",
    answer:
      "Enter your postcode and select your service to see exact pricing for your area. The booking form shows a detailed cost breakdown including all applicable charges before you confirm. You'll always know the total cost upfront.",
  },
  {
    question: "Are materials included in the price?",
    answer:
      "Basic consumables and small materials are typically included. For services requiring specific parts or materials (like plumbing fixtures, electrical components, or specialized supplies), these are charged separately. Professionals will discuss and get your approval before purchasing any materials.",
  },
  {
    question: "Can I tip my professional?",
    answer:
      "Tipping is appreciated but never required. If you're happy with the service, you can add a tip through the platform after job completion. 100% of tips go directly to your professional.",
  },
];

const PricingFAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-12 sm:py-16 md:py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#fe7a14]/10 to-[#ff8c3a]/10 rounded-xl sm:rounded-2xl mb-4 sm:mb-5 md:mb-6">
            <i className="ri-question-answer-line text-xl sm:text-2xl md:text-3xl text-[#fe7a14]"></i>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            Pricing FAQs
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-2">
            Everything you need to know about our rates, fees, and payment
            process
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-3 sm:space-y-4">
          {faqData.map((faq, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 sm:border-2 rounded-lg sm:rounded-xl overflow-hidden transition-all duration-300 hover:border-[#fe7a14]/30 hover:shadow-md sm:hover:shadow-lg"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-4 sm:px-5 md:px-6 py-3.5 sm:py-4 md:py-5 flex items-center justify-between text-left cursor-pointer group"
                aria-expanded={openIndex === index}
              >
                <div className="flex items-start gap-2.5 sm:gap-3 md:gap-4 flex-1">
                  <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 flex items-center justify-center bg-gradient-to-br from-[#fe7a14]/10 to-[#ff8c3a]/10 rounded-md sm:rounded-lg flex-shrink-0 mt-0.5 group-hover:from-[#fe7a14]/20 group-hover:to-[#ff8c3a]/20 transition-all">
                    <i className="ri-question-line text-[#fe7a14] text-sm sm:text-base md:text-lg"></i>
                  </div>
                  <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 pr-2 sm:pr-4 group-hover:text-[#fe7a14] transition-colors leading-snug">
                    {faq.question}
                  </h3>
                </div>
                <div
                  className={`w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 flex items-center justify-center bg-gray-100 rounded-md sm:rounded-lg flex-shrink-0 transition-all duration-300 group-hover:bg-[#fe7a14] ${
                    openIndex === index ? "rotate-180 bg-[#fe7a14]" : ""
                  }`}
                >
                  <i
                    className={`ri-arrow-down-s-line text-base sm:text-lg md:text-xl transition-colors ${
                      openIndex === index
                        ? "text-white"
                        : "text-gray-600 group-hover:text-white"
                    }`}
                  ></i>
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? "max-h-96" : "max-h-0"
                }`}
              >
                <div className="px-4 sm:px-5 md:px-6 pb-4 sm:pb-5 md:pb-6 pl-[52px] sm:pl-[60px] md:pl-[72px]">
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-md sm:rounded-lg p-3 sm:p-4 md:p-5 border-l-2 sm:border-l-4 border-[#fe7a14]">
                    <p className="text-gray-700 leading-relaxed text-xs sm:text-sm md:text-base">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Still Have Questions CTA */}
        <div className="mt-10 sm:mt-12 md:mt-16 bg-gradient-to-br from-[#0F2A44] to-[#1a4d7a] rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 lg:p-12 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-[#fe7a14] rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-5 md:mb-6">
              <i className="ri-customer-service-2-line text-xl sm:text-2xl md:text-3xl text-white"></i>
            </div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 sm:mb-4">
              Still Have Questions?
            </h3>
            <p className="text-sm sm:text-base md:text-lg text-white/90 mb-5 sm:mb-6 md:mb-8 px-2">
              Our support team is here to help you understand our pricing and
              find the right service for your needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <a
                href="/contact"
                className="px-5 sm:px-6 md:px-8 py-3 sm:py-3.5 md:py-4 bg-[#fe7a14] text-white font-bold rounded-lg hover:bg-[#e56a0f] transition-all hover:scale-105 hover:shadow-xl whitespace-nowrap inline-flex items-center justify-center gap-2 cursor-pointer text-sm sm:text-base"
              >
                <i className="ri-mail-line text-lg sm:text-xl"></i>
                Contact Support
              </a>
              <a
                href="/"
                className="px-5 sm:px-6 md:px-8 py-3 sm:py-3.5 md:py-4 bg-white text-[#0F2A44] font-bold rounded-lg hover:bg-gray-100 transition-all hover:scale-105 hover:shadow-xl whitespace-nowrap inline-flex items-center justify-center gap-2 cursor-pointer text-sm sm:text-base"
              >
                <i className="ri-calendar-check-line text-lg sm:text-xl"></i>
                Book a Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingFAQ;
